// ---------------------------------------------------------------------------
// CRM-T.4: "What Happened While Away" — catch-up page after absence
// ---------------------------------------------------------------------------
import { useState, useEffect } from "react";
import { Link } from "react-router";
import { apiFetch } from "@/lib/api-client";
import { Clock, MessageSquare, TrendingUp, AlertTriangle, DollarSign, Zap, Send, Check, ArrowRight } from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { formatCurrency, formatNumber } from "@/lib/i18n";

interface AwaySummary {
  since: string;
  hoursAway: number;
  messagesNeedingReply: Array<{ id: string; contactName: string; preview: string; channel: string; aiDraft: string; }>;
  dealChanges: Array<{ dealId: string; title: string; change: string; value: number; }>;
  atRiskContacts: Array<{ contactId: string; name: string; reason: string; riskScore: number; }>;
  revenue: { newRevenue: number; currency: string; newBookings: number; };
  autoActions: Array<{ id: string; description: string; status: "completed" | "pending_approval"; }>;
}

interface AuthSessionSummary {
  id: string;
  createdAt: string;
  updatedAt?: string;
  current: boolean;
}

function formatRevenueAmount(amount: number, currency: string): string {
  return formatCurrency(amount * 100, currency, { maximumFractionDigits: 0 });
}

function formatSignedNumber(value: number): string {
  if (value === 0) {
    return formatNumber(0);
  }

  const formattedValue = formatNumber(Math.abs(value));
  return value > 0 ? `+${formattedValue}` : `-${formattedValue}`;
}

async function getPreviousSessionTimestamp(): Promise<string | null> {
  try {
    const response = await apiFetch<{ data: AuthSessionSummary[] }>("/auth/user/sessions");
    const previousSession = response.data
      .filter((session) => !session.current)
      .map((session) => ({
        timestampSource: session.updatedAt ?? session.createdAt,
        timestamp: new Date(session.updatedAt ?? session.createdAt).getTime(),
      }))
      .filter((session) => !Number.isNaN(session.timestamp))
      .sort((left, right) => right.timestamp - left.timestamp)[0];

    return previousSession?.timestampSource ?? null;
  } catch {
    return null;
  }
}

export default function AwaySummaryPage() {
  const [summary, setSummary] = useState<AwaySummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;

    const loadSummary = async () => {
      try {
        const previousSessionTimestamp = await getPreviousSessionTimestamp();
        const query = previousSessionTimestamp
          ? `?since=${encodeURIComponent(previousSessionTimestamp)}`
          : "";
        const response = await apiFetch<{ data: AwaySummary }>(`/crm/intelligence/away-summary${query}`);
        if (!isCancelled) {
          setSummary(response.data ?? null);
        }
      } catch {
        if (!isCancelled) {
          setSummary(null);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadSummary();

    return () => {
      isCancelled = true;
    };
  }, []);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="grid grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonCard key={i} className="h-24" />
          ))}
        </div>
        <SkeletonCard className="h-48" />
        <SkeletonCard className="h-40" />
      </div>
    );
  }
  if (!summary) return <div className="text-center py-16 text-sm text-[var(--color-muted-foreground)]">Nothing happened while you were away.</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Welcome back</h1>
        <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
          Here's what happened in the last {summary.hoursAway} hours
        </p>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "Messages", value: summary.messagesNeedingReply.length, icon: MessageSquare, color: "var(--color-primary)" },
          { label: "Deal changes", value: summary.dealChanges.length, icon: TrendingUp, color: "var(--color-success)" },
          { label: "At risk", value: summary.atRiskContacts.length, icon: AlertTriangle, color: "var(--color-warning)" },
          { label: "Revenue", value: formatRevenueAmount(summary.revenue.newRevenue, summary.revenue.currency), icon: DollarSign, color: "var(--color-success)" },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
            <div className="flex items-center gap-1.5 text-[10px] text-[var(--color-muted-foreground)]"><Icon className="h-3 w-3" style={{ color }} /> {label}</div>
            <p className="text-lg font-bold text-[var(--color-foreground)] mt-0.5 tabular-nums">{value}</p>
          </div>
        ))}
      </div>

      {/* Messages needing reply */}
      {summary.messagesNeedingReply.length > 0 && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
          <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-[var(--color-primary)]" />
            <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Messages needing reply ({summary.messagesNeedingReply.length})</h3>
          </div>
          <div className="divide-y divide-[var(--color-border)]">
            {summary.messagesNeedingReply.map((msg) => (
              <div key={msg.id} className="stagger-item px-4 py-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-[var(--color-foreground)]">{msg.contactName}</p>
                  <span className="text-[10px] text-[var(--color-muted-foreground)] capitalize">{msg.channel}</span>
                </div>
                <p className="text-xs text-[var(--color-muted-foreground)]">{msg.preview}</p>
                {msg.aiDraft && (
                  <div className="rounded-lg bg-[var(--color-ai)]/5 border border-[var(--color-ai)]/20 p-2.5">
                    <p className="text-[10px] text-[var(--color-ai)] font-medium mb-1">AI-drafted reply</p>
                    <p className="text-xs text-[var(--color-foreground)]">{msg.aiDraft}</p>
                    <div className="flex gap-2 mt-2">
                      <button className="btn-press rounded-md px-3 py-1 text-[10px] font-medium text-white" style={{ background: "var(--gradient-energy)" }}>
                        <Send className="inline h-2.5 w-2.5 mr-0.5" /> Send as-is
                      </button>
                      <Link to={`/inbox/${msg.id}`} className="rounded-md px-3 py-1 text-[10px] text-[var(--color-muted-foreground)] border border-[var(--color-border)] hover:bg-[var(--color-muted)]">
                        Edit first
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Deal changes */}
      {summary.dealChanges.length > 0 && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
          <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-[var(--color-success)]" />
            <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Deal changes</h3>
          </div>
          <div className="divide-y divide-[var(--color-border)]">
            {summary.dealChanges.map((deal) => (
              <Link key={deal.dealId} to={`/crm/deals/${deal.dealId}`} className="stagger-item flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-[var(--color-muted)]/20">
                <div>
                  <p className="text-sm font-medium text-[var(--color-foreground)]">{deal.title}</p>
                  <p className="text-xs text-[var(--color-muted-foreground)]">{deal.change}</p>
                </div>
                <span className="text-sm font-medium text-[var(--color-foreground)] tabular-nums">{formatSignedNumber(deal.value)}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* At risk */}
      {summary.atRiskContacts.length > 0 && (
        <div className="rounded-xl border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 p-4 space-y-2">
          <h3 className="text-sm font-semibold text-[var(--color-foreground)] flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4 text-[var(--color-warning)]" /> At-risk contacts
          </h3>
          {summary.atRiskContacts.map((c) => (
            <Link key={c.contactId} to={`/crm/contacts/${c.contactId}`} className="stagger-item flex items-center justify-between py-1 text-xs hover:underline">
              <span className="text-[var(--color-foreground)]">{c.name} — {c.reason}</span>
              <span className="text-[var(--color-warning)] font-medium tabular-nums">{c.riskScore}% risk</span>
            </Link>
          ))}
        </div>
      )}

      {/* Auto-actions */}
      {summary.autoActions.length > 0 && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
          <h3 className="text-sm font-semibold text-[var(--color-foreground)] flex items-center gap-1.5 mb-3">
            <Zap className="h-4 w-4 text-[var(--color-ai)]" /> Auto-actions taken
          </h3>
          <div className="space-y-1.5">
            {summary.autoActions.map((a) => (
              <div key={a.id} className="stagger-item flex items-center gap-2 text-xs">
                {a.status === "completed" ? <Check className="h-3 w-3 text-[var(--color-success)]" /> : <Clock className="h-3 w-3 text-[var(--color-warning)]" />}
                <span className="text-[var(--color-foreground)]">{a.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-[var(--color-primary)] hover:underline">
        Go to dashboard <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}
